import maplibregl from "maplibre-gl"

import Hook from "../../utils/hook"

export const GuidesPageMap = Hook(`GuidesPageMap`, {
  values: { data: Array },
  refs: [`maplibre`],
  hoveredFeatureId: null,
  mouseMoveHandler: null,
  mouseLeaveHandler: null,
  popup: null,

  mounted() {
    this.layerIds = new Set()
    this.sourceIds = new Set()
  },

  destroyed() {
    this.clear()
  },

  maplibreRefMounted(maplibreRef) {
    if (this.isMapLibreReady()) this.draw()
    else maplibreRef.addLoadHandler(this.draw.bind(this))
  },

  isMapLibreReady() {
    if (!this.maplibreRef) return false
    return this.maplibreRef.map?.isStyleLoaded?.()
  },

  dataValueChanged() {
    this.draw()
  },

  draw() {
    if (!this.isMapLibreReady()) return
    this.clear()
    this.addSourcesAndLayers()
    this.addMapHandlers()
  },

  addSourcesAndLayers() {
    if (!this.maplibreRef) return

    const sourceId = `guides-source`
    const layerId = `guides-layer`
    const features = this.dataValue
      .filter(({ lat, lng }) => Number.isFinite(lat) && Number.isFinite(lng))
      .map(({ id, title, lat, lng, path }) => ({
        type: `Feature`,
        id,
        geometry: {
          type: `Point`,
          coordinates: [lng, lat],
        },
        properties: { id, title, path },
      }))

    this.maplibreRef.map.addSource(sourceId, {
      type: `geojson`,
      data: {
        type: `FeatureCollection`,
        features,
      },
    })
    this.sourceIds.add(sourceId)

    this.maplibreRef.map.addLayer({
      id: layerId,
      source: sourceId,
      type: `symbol`,
      layout: {
        "icon-image": [
          `case`,
          [`==`, [`id`], [`global-state`, `guidesHoveredFeatureId`]],
          `none-active`,
          `none`,
        ],
        "icon-size": 0.5,
        "icon-allow-overlap": true,
        "icon-anchor": `bottom`,
        "symbol-z-order": `source`,
        "symbol-sort-key": [
          `case`,
          [`==`, [`id`], [`global-state`, `guidesHoveredFeatureId`]],
          1,
          0,
        ],
      },
    })
    this.layerIds.add(layerId)
  },

  addMapHandlers() {
    if (!this.maplibreRef) return

    const map = this.maplibreRef.map

    this.clickHandler = (event) => {
      const path = event.features?.[0]?.properties?.path
      if (path) window.location.href = path
    }

    this.mouseMoveHandler = (event) => {
      const feature = event.features?.[0]
      const featureId = feature?.id
      if (featureId == null || featureId === this.hoveredFeatureId) return

      this.hoveredFeatureId = featureId
      map.setGlobalStateProperty(`guidesHoveredFeatureId`, featureId)
      map.getCanvas().style.cursor = `pointer`

      this.popup?.remove()
      this.popup = new maplibregl.Popup({
        offset: 30,
        anchor: `bottom`,
        closeButton: false,
        closeOnClick: false,
      })
        .setLngLat(feature.geometry.coordinates)
        .setText(feature.properties.title)
        .addTo(map)
    }

    this.mouseLeaveHandler = () => {
      this.hoveredFeatureId = null
      map.setGlobalStateProperty(`guidesHoveredFeatureId`, -1)
      map.getCanvas().style.cursor = ``
      this.popup?.remove()
      this.popup = null
    }

    map.on(`mousemove`, `guides-layer`, this.mouseMoveHandler)
    map.on(`mouseleave`, `guides-layer`, this.mouseLeaveHandler)
    map.on(`click`, `guides-layer`, this.clickHandler)
  },

  clear() {
    let map = null
    if (this.maplibreRef) map = this.maplibreRef.map

    if (this.isMapLibreReady()) {
      map.setGlobalStateProperty(`guidesHoveredFeatureId`, -1)

      Array.from(this.layerIds).forEach((id) => {
        if (map.getLayer(id)) map.removeLayer(id)
      })

      Array.from(this.sourceIds).forEach((id) => {
        if (map.getSource(id)) map.removeSource(id)
      })
    }

    if (map) {
      map.getCanvas().style.cursor = ``

      if (this.mouseMoveHandler)
        map.off(`mousemove`, `guides-layer`, this.mouseMoveHandler)

      if (this.mouseLeaveHandler)
        map.off(`mouseleave`, `guides-layer`, this.mouseLeaveHandler)

      if (this.clickHandler) map.off(`click`, `guides-layer`, this.clickHandler)
    }

    this.layerIds.clear()
    this.sourceIds.clear()
    this.popup?.remove()
    this.popup = null
    this.hoveredFeatureId = null
    this.mouseMoveHandler = null
    this.mouseLeaveHandler = null
    this.clickHandler = null
  },
})
